import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Menu, X, PanelLeftIcon, PanelRightIcon } from "lucide-react";
import { getIconForSection, getIconForLinkedEntity, type NavItemWithIcon, type LinkedEntityWithIcon } from "@/lib/sidebar-icons";

interface NavItem extends NavItemWithIcon {}

interface LinkedEntity extends LinkedEntityWithIcon {}

interface TabDef {
  value: string;
  label: string;
  content: React.ReactNode;
}

interface Breadcrumb {
  label: string;
  href?: string;
}

interface DetailPageShellProps {
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  badge?: React.ReactNode;
  extraContent?: React.ReactNode; // Extra content between breadcrumbs and tabs (e.g., progress bars)
  navItems: NavItem[];
  linkedEntities?: LinkedEntity[];
  tabs: TabDef[];
  rightPanel: React.ReactNode;
  actions?: React.ReactNode;
  defaultTab?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  onNavItemClick?: (href: string) => void;
  leftPanelCollapsible?: boolean; // Default: true
  leftPanelDefaultCollapsed?: boolean; // Default: false
  children?: React.ReactNode; // For modals and other overlay components
}

export function DetailPageShell({
  title,
  subtitle,
  breadcrumbs,
  badge,
  extraContent,
  navItems,
  linkedEntities,
  tabs,
  rightPanel,
  actions,
  defaultTab,
  value,
  onValueChange,
  onNavItemClick,
  leftPanelCollapsible = true,
  leftPanelDefaultCollapsed = false,
  children,
}: DetailPageShellProps) {
  const [, setLocation] = useLocation();
  const [leftPanelOpen, setLeftPanelOpen] = useState(false);
  const [leftPanelCollapsed, setLeftPanelCollapsed] = useState(leftPanelDefaultCollapsed);

  // Optional: Cookie persistence for collapse state
  useEffect(() => {
    const savedState = document.cookie
      .split('; ')
      .find(row => row.startsWith('detail_sidebar_state='))
      ?.split('=')[1];
    if (savedState !== undefined) {
      setLeftPanelCollapsed(savedState === 'collapsed');
    }
  }, []);

  const toggleLeftPanelCollapse = () => {
    const newState = !leftPanelCollapsed;
    setLeftPanelCollapsed(newState);

    // Persist state via cookie (7 days, same as main sidebar)
    document.cookie = `detail_sidebar_state=${newState ? 'collapsed' : 'expanded'}; path=/; max-age=${60 * 60 * 24 * 7}`;
  };

  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    if (onNavItemClick) {
      onNavItemClick(href);
    }
  };

  const handleBreadcrumbClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string | undefined) => {
    if (!href) return;
    e.preventDefault();
    setLocation(href);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Page Header */}
      <div className="flex items-center justify-between p-3 border-b bg-card">
        <div className="flex items-center gap-2">
          {/* Mobile menu toggle */}
          <Button
            size="icon"
            variant="ghost"
            className="lg:hidden h-7 w-7"
            onClick={() => setLeftPanelOpen(!leftPanelOpen)}
            data-testid="button-toggle-nav"
          >
            {leftPanelOpen ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </Button>
          
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-semibold">{title}</h1>
            {badge && <div>{badge}</div>}
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          {actions}
        </div>
      </div>

      {/* Extra content area (e.g., progress bars, workflow indicators) */}
      {extraContent && (
        <div className="px-4 py-3 border-b bg-card">
          {extraContent}
        </div>
      )}

      {(breadcrumbs || subtitle) && (
        <div className="px-3 py-1 text-xs text-muted-foreground border-b bg-muted/30">
          {breadcrumbs ? (
            <div className="flex items-center gap-2">
              {breadcrumbs.map((crumb, index) => (
                <div key={index} className="flex items-center gap-2">
                  {crumb.href ? (
                    <a
                      href={crumb.href}
                      onClick={(e) => handleBreadcrumbClick(e, crumb.href)}
                      className="hover:text-foreground hover-elevate active-elevate-2 transition-colors truncate max-w-[200px]"
                      title={crumb.label}
                      data-testid={`breadcrumb-${crumb.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {crumb.label}
                    </a>
                  ) : (
                    <span className="truncate max-w-[200px]" title={crumb.label}>{crumb.label}</span>
                  )}
                  {index < breadcrumbs.length - 1 && <span className="text-muted-foreground/50">/</span>}
                </div>
              ))}
            </div>
          ) : (
            subtitle
          )}
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left Navigation Panel - Collapsible (180px ↔ 60px) */}
        <aside
          className={`
            ${leftPanelOpen ? 'block' : 'hidden'} lg:block
            border-r bg-card overflow-y-auto
            absolute lg:relative z-10 lg:z-0
            h-[calc(100vh-120px)] lg:h-auto
            transition-all duration-200 ease-linear
            ${leftPanelCollapsed
              ? 'w-full lg:w-[60px]'  // Collapsed: 60px
              : 'w-full lg:w-[180px]' // Expanded: 180px
            }
          `}
          data-state={leftPanelCollapsed ? 'collapsed' : 'expanded'}
          data-testid="panel-left-nav"
        >
          {/* Toggle button - Desktop only */}
          {leftPanelCollapsible && (
            <div className="hidden lg:flex justify-end p-2 border-b">
              <Button
                size="icon"
                variant="ghost"
                onClick={toggleLeftPanelCollapse}
                aria-label={leftPanelCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                aria-expanded={!leftPanelCollapsed}
                className="h-7 w-7"
                data-testid="button-toggle-left-panel"
              >
                {leftPanelCollapsed ? (
                  <PanelRightIcon className="h-4 w-4" />
                ) : (
                  <PanelLeftIcon className="h-4 w-4" />
                )}
              </Button>
            </div>
          )}

          <nav className="p-3">
            {/* QUICK NAV section */}
            {!leftPanelCollapsed ? (
              // Expanded state (current implementation)
              <>
                <div className="text-xs font-semibold text-muted-foreground mb-2">
                  QUICK NAV
                </div>
                <ul className="space-y-1">
                  {navItems.map((item) => (
                    <li key={item.href}>
                      <a
                        href={item.href}
                        onClick={(e) => handleNavClick(e, item.href)}
                        className="flex items-center justify-between px-2 py-1 text-sm hover-elevate active-elevate-2 rounded-sm"
                        data-testid={`link-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <span>{item.label}</span>
                        {item.count !== undefined && (
                          <span className="text-xs text-muted-foreground">
                            {item.count}
                          </span>
                        )}
                      </a>
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              // Collapsed state (icon-only)
              <TooltipProvider>
                <ul className="space-y-2">
                  {navItems.map((item) => {
                    const IconComponent = getIconForSection(item.label, item.icon);
                    return (
                      <li key={item.href}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <a
                              href={item.href}
                              onClick={(e) => handleNavClick(e, item.href)}
                              className="flex items-center justify-center p-2 hover-elevate active-elevate-2 rounded-sm relative"
                              aria-label={item.label}
                              data-testid={`link-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <IconComponent className="h-5 w-5" />
                              {item.count !== undefined && (
                                <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs px-1 rounded">
                                  {item.count}
                                </span>
                              )}
                            </a>
                          </TooltipTrigger>
                          <TooltipContent side="right">{item.label}</TooltipContent>
                        </Tooltip>
                      </li>
                    );
                  })}
                </ul>
              </TooltipProvider>
            )}

            {/* LINKED section */}
            {linkedEntities && linkedEntities.length > 0 && (
              <>
                <div className="border-t my-2" />
                {!leftPanelCollapsed ? (
                  // Expanded: Full labels
                  <>
                    <div className="text-xs font-semibold text-muted-foreground mb-2">
                      LINKED
                    </div>
                    <ul className="space-y-1">
                      {linkedEntities.map((entity) => (
                        <li key={entity.label}>
                          {entity.href ? (
                            <a
                              href={entity.href}
                              onClick={(e) => {
                                if (onNavItemClick) {
                                  e.preventDefault();
                                  onNavItemClick(entity.href!);
                                }
                              }}
                              className="flex items-center justify-between px-2 py-1 text-sm hover-elevate active-elevate-2 rounded-sm"
                              data-testid={`link-linked-${entity.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <span>{entity.label}</span>
                              <span className="text-xs text-muted-foreground">{entity.count} →</span>
                            </a>
                          ) : (
                            <div className="flex items-center justify-between px-2 py-1 text-sm">
                              <span>{entity.label}</span>
                              <span className="text-xs text-muted-foreground">{entity.count}</span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  // Collapsed: Count badges only with icons
                  <TooltipProvider>
                    <ul className="space-y-2">
                      {linkedEntities.map((entity) => {
                        const IconComponent = getIconForLinkedEntity(entity.label, entity.icon);
                        return (
                          <li key={entity.label}>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                {entity.href ? (
                                  <a
                                    href={entity.href}
                                    onClick={(e) => {
                                      if (onNavItemClick) {
                                        e.preventDefault();
                                        onNavItemClick(entity.href!);
                                      }
                                    }}
                                    className="flex items-center justify-center p-2 hover-elevate active-elevate-2 rounded-sm relative"
                                    aria-label={entity.label}
                                    data-testid={`link-linked-${entity.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  >
                                    <IconComponent className="h-5 w-5" />
                                    <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs px-1 rounded">
                                      {entity.count}
                                    </span>
                                  </a>
                                ) : (
                                  <div className="flex items-center justify-center p-2 relative">
                                    <IconComponent className="h-5 w-5" />
                                    <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-xs px-1 rounded">
                                      {entity.count}
                                    </span>
                                  </div>
                                )}
                              </TooltipTrigger>
                              <TooltipContent side="right">{entity.label}</TooltipContent>
                            </Tooltip>
                          </li>
                        );
                      })}
                    </ul>
                  </TooltipProvider>
                )}
              </>
            )}
          </nav>
        </aside>

        {/* Main Content - Fluid */}
        <main className="flex-1 overflow-hidden flex flex-col">
          <Tabs 
            value={value} 
            onValueChange={onValueChange}
            defaultValue={defaultTab || tabs[0]?.value} 
            className="flex-1 flex flex-col overflow-hidden"
          >
            <TabsList className="w-full justify-start rounded-none border-b bg-transparent px-3 h-10 shrink-0">
              {tabs.map((tab) => (
                <TabsTrigger
                  key={tab.value}
                  value={tab.value}
                  className="data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none h-10 text-sm hover:bg-accent/50 hover:text-accent-foreground transition-colors cursor-pointer"
                  data-testid={`tab-${tab.value}`}
                >
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
            
            <div className="flex-1 min-h-0 overflow-y-auto">
              {tabs.map((tab) => (
                <TabsContent
                  key={tab.value}
                  value={tab.value}
                  className="mt-0 p-3 space-y-4"
                  data-testid={`tab-content-${tab.value}`}
                >
                  {tab.content}
                </TabsContent>
              ))}
            </div>
          </Tabs>
        </main>

        {/* Right Panel - 260px */}
        <aside
          className="hidden xl:block xl:w-[260px] border-l bg-card overflow-y-auto"
          data-testid="panel-right-info"
        >
          <div className="p-3">
            {rightPanel}
          </div>
        </aside>
      </div>

      {/* Children (modals, dialogs, etc.) */}
      {children}
    </div>
  );
}
